import React, { useRef, useState, useEffect, useMemo } from 'react';
import CraftRenderer from '@builder4/shared/components/CraftRenderer';
import type { RenderContextType } from '@builder4/shared/context/RenderContext';
import { DESKTOP_WIDTH } from '@builder4/shared/utils/constants';
import { css } from '@emotion/css';
import { componentResolver } from '@builder4/shared/components/componentResolver';
import { type CraftJsonDto } from '@builder4/shared/dto/craft';
import { SerializedNodeDto } from '@builder4/shared/dto/craft/SerializedNodeDto';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { Skeleton } from '@/components/ui/skeleton';
import { ROOT_NODE, useEditor } from '@craftjs/core';
import { plainToClass } from 'class-transformer';

const popupThumbnailStyles = css`
  .popup-dialog {
    display: block;
    position: relative;
  }
`;

interface SectionThumbnailProps {
  sectionData: CraftJsonDto;
}

export const SectionThumbnail: React.FC<SectionThumbnailProps> = ({ sectionData }) => {
  const activeGlobalStyle = useAppSelector(selectActiveGlobalStyle);

  const context: RenderContextType = useMemo(
    () => ({
      environment: 'server',
      isEditable: false,
      styles: activeGlobalStyle,
    }),
    [activeGlobalStyle],
  );

  const { pageRootNode } = useEditor((_, query) => {
    return {
      pageRootNode: query.node(ROOT_NODE).toSerializedNode(),
    };
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);
    const resize = () => {
      if (!containerRef.current || !measureRef.current) return;
      const containerWidth = containerRef.current.getBoundingClientRect().width;
      setScale(containerWidth / DESKTOP_WIDTH);
      setIsReady(true);
    };

    resize();
    const observer = new ResizeObserver(resize);
    if (containerRef.current) observer.observe(containerRef.current);
    if (measureRef.current) observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [sectionData]);

  return (
    <div ref={containerRef} className="relative flex h-full w-full items-center justify-center">
      {!isReady ? <Skeleton className="absolute inset-0 rounded-md" /> : null}
      <div
        style={{
          zoom: scale,
        }}
        className={
          isReady ? 'relative transition-opacity' : 'relative opacity-0 transition-opacity'
        }
      >
        <div style={{ width: DESKTOP_WIDTH }} ref={measureRef} className={popupThumbnailStyles}>
          <CraftRenderer
            craftJson={sectionData.forThumbnailRender(
              plainToClass(SerializedNodeDto, pageRootNode),
            )}
            componentResolver={componentResolver}
            context={context}
          />
        </div>
      </div>
      {/* Click shield overlay */}
      <div className="absolute inset-0 z-10 cursor-pointer" />
    </div>
  );
};
